/** /org/admin — Permisos (roster ORG_GOVERNANCE), Herramientas y Políticas (T5) y Salud del servicio (corridas del sync, conteos, alias pendientes, higiene). */
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { GovernancePanel } from '@/components/governance/GovernancePanel';
import { InternalTable, EmptyState, LoaderBlock } from '@/components/internal/shared';
import { EnumBadge } from '@/components/ui/enum-badge';
import { useOrgAccess, useOrgCommunityPicker, useOrgGovernance, useOrgHealth, useSaveOrgGovernance } from '@/hooks/useOrg';
import { fmtRelative } from '@/lib/relative-time';
import { OrgToolsTab } from '@/components/org/OrgToolsTab';
import { OrgPoliciesTab } from '@/components/org/OrgPoliciesTab';

export function OrgAdminPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab') ?? 'governance';
  const tab = raw === 'health' || raw === 'tools' || raw === 'policies' ? raw : 'governance';
  const access = useOrgAccess();
  if (access.isLoading) return <LoaderBlock />;
  if (!access.data?.canAdmin) {
    return (
      <div className="mx-auto mt-16 max-w-xl px-4">
        <Alert>
          <Lock className="h-4 w-4" />
          <AlertTitle>{t('org.admin.no_access_title', 'Sin acceso a la administración del Org Service')}</AlertTitle>
          <AlertDescription>{t('org.admin.no_access_body', 'La administran los admins de la plataforma y las personas del roster de permisos del Org Service.')}</AlertDescription>
        </Alert>
      </div>
    );
  }
  return (
    <div className="space-y-4 pb-24">
      <div>
        <h1 className="text-2xl font-bold">{t('org.admin.title', 'Administración del Org Service')}</h1>
        <p className="text-sm text-muted-foreground">{t('org.admin.subtitle', 'Quién ve todas las asignaciones y cómo viene el sync diario desde la app de Carga de Horas.')}</p>
      </div>
      <Tabs value={tab} onValueChange={(v) => setParams({ tab: v })}>
        <TabsList className="flex h-auto flex-wrap">
          <TabsTrigger value="governance">{t('org.tabs.governance', 'Permisos')}</TabsTrigger>
          <TabsTrigger value="tools">{t('org.tabs.tools', 'Herramientas')}</TabsTrigger>
          <TabsTrigger value="policies">{t('org.tabs.policies', 'Políticas')}</TabsTrigger>
          <TabsTrigger value="health">{t('org.tabs.health', 'Salud')}</TabsTrigger>
        </TabsList>
        <TabsContent value="governance"><GovernanceTab /></TabsContent>
        <TabsContent value="tools"><OrgToolsTab /></TabsContent>
        <TabsContent value="policies"><OrgPoliciesTab /></TabsContent>
        <TabsContent value="health"><HealthTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function GovernanceTab() {
  const { data: governance, isLoading, isError } = useOrgGovernance();
  const { data: members = [], isLoading: membersLoading } = useOrgCommunityPicker();
  const save = useSaveOrgGovernance();
  return (
    <GovernancePanel
      namespace="org.governance"
      governance={governance}
      isLoading={isLoading}
      isError={isError}
      members={members}
      membersLoading={membersLoading}
      onSave={(payload, onSuccess) => save.mutate(payload, { onSuccess })}
      saving={save.isPending}
    />
  );
}

function HealthTab() {
  const { t, i18n } = useTranslation();
  const q = useOrgHealth(true);
  if (q.isLoading) return <LoaderBlock />;
  if (q.isError || !q.data) return <EmptyState title={t('org.admin.health_error', 'No se pudo leer el schema org. Si el sync todavía no corrió, las tablas no existen: invocá la Lambda del sync una vez.')} />;
  const { periods, runs, aliases, lastIssues } = q.data;
  type Period = (typeof periods)[number];
  type Run = (typeof runs)[number];
  type Alias = (typeof aliases)[number];
  type Issue = (typeof lastIssues)[number];
  const periodCols = [
    { key: 'period', label: t('org.admin.col_period', 'Mes'), render: (p: Period) => p.period },
    { key: 'assignments', label: t('org.admin.col_assignments', 'Asignaciones'), align: 'right' as const, render: (p: Period) => p.assignments },
    { key: 'persons', label: t('org.admin.col_persons', 'Personas'), align: 'right' as const, render: (p: Period) => p.persons },
    { key: 'clients', label: t('org.admin.col_clients', 'Clientes'), align: 'right' as const, render: (p: Period) => p.clients },
    { key: 'confirmed', label: t('org.admin.col_confirmed', 'Confirmadas'), align: 'right' as const, render: (p: Period) => p.confirmed },
  ];
  const runCols = [
    { key: 'id', label: '#', render: (r: Run) => r.id },
    { key: 'source', label: t('org.admin.col_source', 'Fuente'), render: (r: Run) => <span className="text-muted-foreground">{t(`org.sync.${r.source}`, r.source)}</span> },
    { key: 'period', label: t('org.admin.col_period', 'Mes'), render: (r: Run) => r.period },
    { key: 'started', label: t('org.admin.col_started', 'Corrió'), render: (r: Run) => <span className="text-muted-foreground">{fmtRelative(r.started_at, i18n.language)}</span> },
    { key: 'ok', label: t('org.admin.col_ok', 'Resultado'), render: (r: Run) => (r.ok === null ? <EnumBadge value="running" tone="warning" label={t('org.admin.run_running', 'En curso')} /> : r.ok ? <EnumBadge value="ok" tone="success" label={t('org.admin.run_ok', 'OK')} /> : <EnumBadge value="error" tone="danger" label={t('org.admin.run_error', 'Falló')} />) },
    { key: 'stats', label: t('org.admin.col_stats', 'Creadas / actualizadas / confirmadas / cerradas'), render: (r: Run) => <span className="text-muted-foreground">{r.stats ? `${r.stats.created} / ${r.stats.updated} / ${r.stats.confirmed} / ${r.stats.closed}` : '—'}</span> },
    { key: 'issues', label: t('org.admin.col_issues', 'Avisos'), align: 'right' as const, render: (r: Run) => r.issues.length },
  ];
  const aliasCols = [
    { key: 'name', label: t('org.admin.col_alias', 'Nombre en la app'), render: (a: Alias) => a.name },
    { key: 'persons', label: t('org.admin.col_alias_persons', 'Personas'), align: 'right' as const, render: (a: Alias) => a.persons },
    { key: 'last', label: t('org.admin.col_last_seen', 'Visto por última vez'), render: (a: Alias) => <span className="text-muted-foreground">{a.last_seen}</span> },
  ];
  const issueCols = [
    { key: 'source', label: t('org.admin.col_source', 'Fuente'), render: (i: Issue) => <span className="text-muted-foreground">{t(`org.sync.${i.source}`, i.source)}</span> },
    { key: 'kind', label: t('org.admin.col_kind', 'Tipo'), render: (i: Issue) => <EnumBadge value={i.kind} tone={i.kind === 'sync_failed' ? 'danger' : 'warning'} label={t(`org.hygiene.${i.kind}`, i.kind)} /> },
    { key: 'detail', label: t('org.admin.col_detail', 'Detalle'), render: (i: Issue) => <span className="text-muted-foreground">{i.detail}{i.count ? ` (${i.count})` : ''}</span> },
  ];
  return (
    <div className="space-y-6">
      <section>
        <h3 className="mb-2 text-sm font-semibold">{t('org.admin.health_periods', 'Asignaciones vigentes por mes')}</h3>
        <InternalTable columns={periodCols} rows={periods} rowKey={(p) => p.period} />
      </section>
      <section>
        <h3 className="mb-2 text-sm font-semibold">{t('org.admin.health_runs', 'Últimas corridas del sync')}</h3>
        {runs.length ? <InternalTable columns={runCols} rows={runs} rowKey={(r) => String(r.id)} /> : <EmptyState title={t('org.admin.health_empty', 'Todavía no corrió ningún sync.')} />}
      </section>
      <section>
        <h3 className="mb-2 text-sm font-semibold">{t('org.admin.health_aliases', 'Clientes de la app sin alta en brain-clients')}</h3>
        <p className="mb-2 text-xs text-muted-foreground">{t('org.admin.aliases_help', 'Estas asignaciones no entran hasta que el cliente exista en brain-clients (alta por PR) o su nombre se agregue como allocatorNames de un cliente existente.')}</p>
        {aliases.length ? <InternalTable columns={aliasCols} rows={aliases} rowKey={(a) => `${a.source}:${a.name}`} /> : <EmptyState title={t('org.admin.aliases_empty', 'Todos los nombres de la app resuelven a un cliente.')} />}
      </section>
      <section>
        <h3 className="mb-2 text-sm font-semibold">{t('org.admin.health_issues', 'Higiene de la última corrida')}</h3>
        {lastIssues.length ? <InternalTable columns={issueCols} rows={lastIssues} rowKey={(i, n) => `${i.kind}:${n}`} /> : <EmptyState title={t('org.admin.issues_empty', 'Sin avisos.')} />}
      </section>
    </div>
  );
}
